import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Toolbar,
  Typography,
} from '@mui/material';

import React, { useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAnalytics } from '@contexts/analyticsContext';

import CustomFilterSelectModal from './customFilterSelectModal';
import DateRangePickerComponent from './dateRangePicker';
import PropertySelectModal from './propertySelectModal';

const noop = () => {};

/**
 * ReportHeader component for displaying the report title, property selection,
 * date range picker, and custom filter buttons.
 *
 * @param {object} props - The component props.
 * @param {boolean} props.isCustomFilterPanelOpen - State of the filter panel in the parent component.
 * @param {function} props.onToggleFilterPanel - Function to toggle the filter panel's visibility.
 */
function ReportHeader({
  withSelectFilters = true,
  withCustomFilterPanel = false,
  isCustomFilterPanelOpen = false,
  onToggleFilterPanel = noop,
  sx = {},
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location || {};
  const {
    selectedProperty,
    loadingProperties,
    propertiesError,
    changeSelectedProperty,
    startDate,
    endDate,
    changeDateRange,
  } = useAnalytics();

  const [openPropertyModal, setOpenPropertyModal] = useState(false);
  const [openCustomFilterModal, setOpenCustomFilterModal] = useState(false);

  const handleOpenCustomFilterModal = () => {
    setOpenCustomFilterModal(true);
  };

  const handleCloseCustomFilterModal = () => {
    setOpenCustomFilterModal(false);
  };

  const handleOpenPropertyModal = () => {
    setOpenPropertyModal(true);
  };

  const handleClosePropertyModal = () => {
    setOpenPropertyModal(false);
  };

  const handlePropertySelectedFromModal = (property) => {
    const newPathname = pathname.replace(
      /\/analytics\/[^/]+/,
      `/analytics/${property.propertyId}`,
    );
    changeSelectedProperty(property);
    handleClosePropertyModal();
    navigate(newPathname);
  };

  const handleFilterSelected = (filter) => {
    handleCloseCustomFilterModal();
    navigate(
      `/analytics/${selectedProperty.propertyId}/detail/custom-filter/${filter._id}`,
    );
  };
  const handleCreateNewFilter = (filter) => {
    handleCloseCustomFilterModal();
    navigate(
      `/analytics/${selectedProperty.propertyId}/detail/custom-filter/new`,
    );
  };

  if (propertiesError) {
    return (
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar>
          <Container sx={{ mt: 4 }}>
            <Alert severity='error'>
              Error loading properties: {propertiesError}
            </Alert>
          </Container>
        </Toolbar>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 2, ...sx }}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Button
            variant='outlined'
            color='inherit'
            onClick={loadingProperties ? noop : handleOpenPropertyModal}
            disabled={loadingProperties}
            endIcon={<ArrowDropDownIcon />}
            sx={{
              minWidth: 200,
              textTransform: 'none',
            }}
          >
            <Typography variant='subtitle1' component='span' noWrap>
              {selectedProperty
                ? selectedProperty.displayName
                : 'Select Property'}
            </Typography>
          </Button>
          {withSelectFilters ? (
            <Button
              variant='outlined'
              color='inherit'
              onClick={loadingProperties ? noop : handleOpenCustomFilterModal}
              startIcon={<FilterListIcon />}
              sx={{ flexShrink: 0 }}
              disabled={loadingProperties}
            >
              <Typography variant='subtitle1' component='span' noWrap>
                Select Filters
              </Typography>
            </Button>
          ) : null}
        </Box>

        <Box sx={{ flexShrink: 0, display: 'flex', gap: 1 }}>
          {withCustomFilterPanel ? (
            <Button
              variant='outlined'
              color='inherit'
              onClick={loadingProperties ? noop : onToggleFilterPanel}
              disabled={loadingProperties}
              sx={{ flexShrink: 0 }}
            >
              {isCustomFilterPanelOpen ? (
                <VisibilityOffIcon />
              ) : (
                <VisibilityIcon />
              )}
            </Button>
          ) : null}

          <DateRangePickerComponent
            disabled={loadingProperties}
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={loadingProperties ? noop : changeDateRange}
          />
          {/*
          <Button
            variant='outlined'
            color='inherit'
            startIcon={<ShareIcon />}
            onClick={() => toast.info('Share feature coming soon!')}
            sx={{ flexShrink: 0 }}
          >
            Share
          </Button>
          */}
        </Box>
      </Toolbar>

      <PropertySelectModal
        open={openPropertyModal}
        onClose={handleClosePropertyModal}
        onPropertySelect={handlePropertySelectedFromModal}
      />
      <CustomFilterSelectModal
        open={openCustomFilterModal}
        onClose={handleCloseCustomFilterModal}
        onFilterSelect={handleFilterSelected}
        onCreateNewFilter={handleCreateNewFilter}
      />
    </Paper>
  );
}

export default ReportHeader;
